//
//  pageGuide.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines page guides, the source of page tours and of seeded help text.
//

import type { Permission } from "src/api/Tmd.dtos";

import {
    cardTourTarget,
    columnTourTarget,
    fieldTourTarget,
    helpTourTargets,
    menuTourTarget
} from "../helpTourTargets";

/** A grid column and what it shows. */
export type PageGuideColumn = { header: string; text: string };

/** A side menu item or navigation card, by its label, and what it opens. */
export type PageGuideItem = { label: string; text: string };

/** A field shown beside a field stop, such as the other lines of an address. */
export type PageGuideCoveredField = { path: string; label: string };

/**
 * The parts a tour can be taken in. The whole tour visits every part in page order.
 */
export const tourChapters = {
    overview: "Overview",
    fields: "Fields",
    list: "List and columns",
    menu: "Side menu",
    areas: "Areas",
    page: "On this page",
    saving: "Saving"
} as const;

export type TourChapter = (typeof tourChapters)[keyof typeof tourChapters];

/**
 * One stop of a page guide, in the order the tour visits it.
 *
 * - `field` points at a grouped form field by property path; `label` is the field's label. Covered
 *   fields are listed in the help text but not visited, as for the other lines of an address.
 * - `grid` visits the page's first row, then each column's header.
 * - `menu` visits each side menu item, and `cards` each navigation card, by label.
 * - `target` points at any other tour target on the page.
 *
 * A stop's chapter defaults from its kind (see {@link chapterOf}).
 */
export type PageGuideStop = { chapter?: TourChapter } & (
    | {
          kind: "field";
          path: string;
          label: string;
          text: string;
          permission?: Permission;
          covers?: PageGuideCoveredField[];
      }
    | { kind: "grid"; title: string; text: string; columns?: PageGuideColumn[] }
    | { kind: "menu"; items: PageGuideItem[] }
    | { kind: "cards"; items: PageGuideItem[] }
    | { kind: "target"; target: string; title: string; text: string; permission?: Permission }
);

/**
 * Describes one page for its tour and its help text.
 */
export type PageGuide = {
    /** What the page is for; the tour's opening step and the help's first paragraph. */
    intro: string;
    stops: PageGuideStop[];
    /** Business rules, such as what makes a save fail. Help text only. */
    rules?: string[];
    /** Anything else worth knowing, such as what happens after saving. Help text only. */
    notes?: string[];
    /**
     * Field paths the page source renders that this page never shows, such as fields a create page
     * hides until the record exists.
     */
    notShown?: string[];
};

/** A form field stop. */
export const field = (
    path: string,
    label: string,
    text: string,
    permission?: Permission
): PageGuideStop => ({ kind: "field", path, label, text, permission });

/** The page's list, with notes on its columns. */
export const grid = (
    text: string,
    columns?: PageGuideColumn[],
    title = "The list"
): PageGuideStop => ({
    kind: "grid",
    title,
    text,
    columns
});

/** A column note for {@link grid}. */
export const column = (header: string, text: string): PageGuideColumn => ({ header, text });

/** A side menu item or navigation card for {@link menu} and {@link cards}. */
export const item = (label: string, text: string): PageGuideItem => ({ label, text });

/** The record's side menu, one stop per item. */
export const menu = (...items: PageGuideItem[]): PageGuideStop => ({ kind: "menu", items });

/** A navigation card page's cards, one stop per card. */
export const cards = (...items: PageGuideItem[]): PageGuideStop => ({ kind: "cards", items });

/**
 * Adds the fields shown beside a field stop, as [property path, label] pairs, so the help text
 * lists them while the tour visits the stop's field only.
 */
export const covering = (
    fieldStop: PageGuideStop,
    ...covered: [path: string, label: string][]
): PageGuideStop =>
    fieldStop.kind === "field"
        ? { ...fieldStop, covers: covered.map(([path, label]) => ({ path, label })) }
        : fieldStop;

/** Places a stop in a chapter other than its kind's. */
export const inChapter = (chapter: TourChapter, guideStop: PageGuideStop): PageGuideStop => ({
    ...guideStop,
    chapter
});

/** Any other tour target. */
export const stop = (
    target: string,
    title: string,
    text: string,
    permission?: Permission
): PageGuideStop => ({ kind: "target", target, title, text, permission });

/** The list filter of a grid page. */
export const filterStop = (text: string) =>
    stop(helpTourTargets.gridFilter, "Finding records", text);

/** The actions beside a grid page's heading. */
export const actionsStop = (text: string, permission?: Permission) =>
    stop(helpTourTargets.gridActions, "Adding records", text, permission);

/** The action bar of a model form. */
export const saveStop = (text: string, permission?: Permission) =>
    stop(helpTourTargets.formActions, "Saving", text, permission);

/** Target ids a stop visits, in order. */
export const stopTargets = (guideStop: PageGuideStop): string[] => {
    switch (guideStop.kind) {
        case "field":
            return [fieldTourTarget(guideStop.path)];
        case "grid":
            return [
                helpTourTargets.gridRow,
                ...(guideStop.columns ?? []).map(item => columnTourTarget(item.header))
            ];
        case "menu":
            return guideStop.items.map(item => menuTourTarget(item.label));
        case "cards":
            return guideStop.items.map(item => cardTourTarget(item.label));
        default:
            return [guideStop.target];
    }
};

/** The shell targets whose stops introduce a page rather than work on it. */
const overviewTargets = new Set<string>([
    helpTourTargets.breadcrumbs,
    helpTourTargets.gridActions,
    helpTourTargets.gridFilter,
    helpTourTargets.pageActions,
    helpTourTargets.pageTitle
]);

/** The chapter a stop belongs to: its own, or the default for its kind and target. */
export const chapterOf = (guideStop: PageGuideStop): TourChapter => {
    if (guideStop.chapter) {
        return guideStop.chapter;
    }

    switch (guideStop.kind) {
        case "field":
            return tourChapters.fields;
        case "grid":
            return tourChapters.list;
        case "menu":
            return tourChapters.menu;
        case "cards":
            return tourChapters.areas;
        default:
            return guideStop.target === helpTourTargets.formActions
                ? tourChapters.saving
                : overviewTargets.has(guideStop.target)
                  ? tourChapters.overview
                  : tourChapters.page;
    }
};

/**
 * Derives a create page's guide from its edit page's, replacing the introduction and notes.
 *
 * Postconditions: Stops are shared, so the create tour visits the same fields; stops for fields a
 * create page does not show are skipped when the tour starts.
 */
export const createGuide = (
    edit: PageGuide,
    intro: string,
    notes: string[],
    options: { omit?: string[]; rules?: string[] } = {}
): PageGuide => ({
    intro,
    stops: edit.stops.filter(
        guideStop => guideStop.kind !== "field" || !options.omit?.includes(guideStop.path)
    ),
    rules: options.rules ?? edit.rules,
    notes,
    notShown: [...(edit.notShown ?? []), ...(options.omit ?? [])]
});
